import React from "react";
import { FaHeart } from "react-icons/fa";
import { IoLocationOutline } from "react-icons/io5";

/* ─── Design tokens matching Param App 2.0 Figma ─── */
const T = {
  orange:      "#F47C20",
  orangeLight: "#FEF0E6",
  orangeMid:   "#FAD4AA",
  cream:       "#FBF8F4",
  cream2:      "#F5EFE6",
  brown:       "#2C1A0E",
  brownMid:    "#6B4226",
  gray:        "#8C7B6B",
  grayLight:   "#EAE4DC",
  white:       "#FFFFFF",
  text:        "#1E1410",
  text2:       "#5A4A3C",
  text3:       "#9A8070",
};

/* ─── Reusable primitives ─── */
const styles = {
  section:     { maxWidth: 900, margin: "0 auto", padding: "0 16px" },
  card:        { background: T.white, borderRadius: 20, boxShadow: "0 2px 16px rgba(44,26,14,0.09)", padding: "28px 32px" },
  cardSm:      { background: T.white, borderRadius: 16, boxShadow: "0 2px 12px rgba(44,26,14,0.07)", padding: "20px 24px" },
  pill:        { display: "inline-block", padding: "4px 14px", borderRadius: 100, background: T.orangeLight, color: T.orange, fontSize: 12, fontWeight: 600, letterSpacing: ".04em" },
  orangeBtn:   { background: T.orange, color: T.white, border: "none", borderRadius: 100, padding: "12px 28px", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  tag:         { padding: "3px 10px", borderRadius: 6, background: T.cream2, color: T.text2, fontSize: 12, fontWeight: 500 },
};

/* ─── Meditating silhouette SVG (reused from app) ─── */
const Silhouette = ({ size = 80, color = T.brown }) => (
  <svg width={size} height={size * 1.2} viewBox="0 0 90 110" fill="none" aria-hidden="true">
    <ellipse cx="45" cy="18" rx="12" ry="12" fill={color} opacity=".9" />
    <path d="M20 70 Q35 45 45 40 Q55 45 70 70 Q60 75 45 75 Q30 75 20 70Z" fill={color} opacity=".9" />
    <path d="M15 72 Q10 85 8 95 Q18 90 28 88 Q25 80 22 72Z" fill={color} opacity=".9" />
    <path d="M75 72 Q80 85 82 95 Q72 90 62 88 Q65 80 68 72Z" fill={color} opacity=".9" />
  </svg>
);

/* ─── Lotus decoration SVG ─── */
const LotusDecor = ({ opacity = 0.07, size = 160 }) => (
  <svg width={size} height={size * 0.55} viewBox="0 0 180 100" aria-hidden="true" style={{ pointerEvents: "none", opacity }}>
    <path d="M90 10 Q100 0 110 10 Q120 20 110 32 Q100 42 90 37 Q80 42 70 32 Q60 20 70 10 Q80 0 90 10Z" fill={T.orange} />
    <path d="M50 22 Q60 12 70 22 Q80 32 70 44 Q60 52 50 46 Q40 52 30 44 Q20 32 30 22 Q40 12 50 22Z" fill={T.orange} />
    <path d="M130 22 Q140 12 150 22 Q160 32 150 44 Q140 52 130 46 Q120 52 110 44 Q100 32 110 22 Q120 12 130 22Z" fill={T.orange} />
  </svg>
);

/* ─── Section label ─── */
const SectionLabel = ({ children }) => (
  <span style={styles.pill}>{children}</span>
);

/* ─── About Us Component ─── */
const AboutUs = () => {
  const founders = [
    {
      name: "Gaurv Parmarthy",
      role: "Founder & CEO",
      education: "IIT Kharagpur · Sivananda YVC",
      image: "/assets/aboutpg/Founder.png",
      initials: "GP",
      linkedin: "https://www.linkedin.com/in/gaurav-bansal-b429b63a/",
    },
    {
      name: "Monika Meena",
      role: "Chief Marketing Officer",
      education: "DU · IIM Lucknow · Sivananda YVC",
      image: "/assets/aboutpg/Cofounder.png",
      initials: "MM",
      linkedin: "https://www.linkedin.com/in/monika-meena-883461b2/",
    },
  ];

  const advisors = [
    { name: "Ajay Dwivedi",  role: "Chief Growth Advisor",    initials: "AD", image: "/assets/aboutpg/ajay.png",    linkedin: "https://www.linkedin.com/in/ajay-kumar-dwivedi-64a443106/" },
    { name: "Prateek Jain",  role: "Chief Strategy Advisor",  initials: "PJ", image: "/assets/aboutpg/prateek.png", linkedin: "https://www.linkedin.com/in/prateek-jain-kgp/" },
    { name: "Aditya Goel",   role: "Chief Technology Advisor", initials: "AG", image: "/assets/aboutpg/aditya.png",  linkedin: "https://www.linkedin.com/in/adityagoel1/" },
  ];

  const patrons = [
    { name: "Malini A.",      initials: "MA", image: "/assets/aboutpg/malini.png",  linkedin: "https://in.linkedin.com/in/malini-adapureddy" },
    { name: "Rohit Arora",    initials: "RA", image: "/assets/aboutpg/rohit.png",   linkedin: "https://ca.linkedin.com/in/rohit-arora-987a0558" },
    { name: "Maanas Dwivedi", initials: "MD", image: "/assets/aboutpg/maanas.png",  linkedin: "https://www.linkedin.com/in/maanas-dwivedi-a5a600a/" },
    { name: "Gaurav Mehra",   initials: "GM", image: "/assets/aboutpg/gaurav.png",  linkedin: "https://in.linkedin.com/in/gaurav-mehra-82221a50" },
  ];

  const values = [
    { title: "Authenticity", desc: "We practice what we preach and believe in inspiring others by our own actions." },
    { title: "Integrity",    desc: "We deliver what we commit and we commit what we can deliver." },
    { title: "Sincerity",    desc: "We give our best in what we do and strive to be our better version every day." },
    { title: "Empathy",      desc: "We understand, support, respect, and empower people around us." },
  ];

  const pillars = [
    {
      title: "Our Mission",
      text: "To enable individuals to live healthier, happier, and more mindful lives through holistic wellness practices rooted in Yoga, Ayurveda, Positive Psychology, and Vedanta.",
      image: "/assets/aboutpg/missionn.png",
    },
    {
      title: "Our Vision",
      text: "To be among the most trusted and respected organizations in inspiring holistic wellness — a global movement that empowers people to live at their highest potential.",
      image: "/assets/aboutpg/vision.png",
    
    },
    {
      title: "Our Goal",
      text: "To positively impact the well-being of 1 million people by 2035 — making holistic wellness accessible, practical, and transformative for everyone.",
      image: "/assets/aboutpg/goal.png",
    
    },
  ];

  /* ─── Avatar component: shows image if available, else initials ─── */
  const Avatar = ({ image, initials, size = 96 }) => (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: T.orangeLight, overflow: "hidden",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, border: `2px solid ${T.orangeMid}`,
    }}>
      {image
        ? <img src={image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.target.style.display = "none"; }} />
        : <span style={{ fontSize: size * 0.28, fontWeight: 600, color: T.orange }}>{initials}</span>
      }
    </div>
  );

  return (
    <div style={{ background: T.cream, color: T.text }}>

      {/* ══ HERO ══ */}
      <section style={{
        background: T.brown,
        padding: "64px 24px 56px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* lotus decorations */}
        <div style={{ position: "absolute", top: -20, left: -30, opacity: 0.06 }}>
          <LotusDecor size={220} opacity={1} />
        </div>
        <div style={{ position: "absolute", bottom: -20, right: -30, transform: "rotate(180deg)", opacity: 0.06 }}>
          <LotusDecor size={220} opacity={1} />
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <Silhouette size={60} color="#F5C880" />
        </div>
        <p style={{ ...styles.pill, background: "rgba(244,124,32,0.18)", color: T.orangeMid, marginBottom: 16 }}>
          About Us
        </p>
        <h1 style={{
          fontSize: "clamp(28px, 5vw, 44px)",
          fontWeight: 400,
          color: "#FFF8EE",
          lineHeight: 1.2,
          maxWidth: 640,
          margin: "0 auto 20px",
        }}>
          Living at the highest potential
        </h1>
        <p style={{ color: "rgba(255,248,238,0.65)", fontSize: 15, maxWidth: 560, margin: "0 auto", lineHeight: 1.7 }}>
          <em>"Param"</em> — a Sanskrit word meaning the highest, the ultimate, the most supreme.
        </p>
      </section>

      {/* ══ ABOUT PARAGRAPHS ══ */}
      <section style={{ ...styles.section, padding: "56px 24px" }}>
        <div style={{ ...styles.card, display: "flex", flexDirection: "column", gap: 20 }}>
          <SectionLabel>Our story</SectionLabel>
          {[
            `At Param Life, we empower people to live at their highest potential — where success doesn't cost health, ambition doesn't make you emotionally void, and happiness isn't an afterthought. We believe the best life isn't about compromise — it's about wholeness, where success, prosperity, purpose, vibrant health, fulfilling relationships, and deep inner joy co-exist.`,
            `Founded by Gaurav and Monika, alumni of IIT Kharagpur and IIM Lucknow, Param Life is not just a start-up but a movement to bring about a Holistic Wellness revolution — a powerful platform for people to learn and adopt holistic wellbeing practices in an easy and practical way to excel in every aspect of life.`,
            `Having been in leadership roles at multiple corporates and realizing that conventional success alone is insufficient for genuine and lasting joy, the founders discovered the secrets of Holistic Wellbeing in Yoga, Ayurveda, Positive Psychology, and Vedanta — under the guidance of Swami Janardhanananda Saraswati, a Yogi and monk in the Himalayas at Uttarkashi.`,
            `After integrating these insights in their own lives and witnessing the impact, they built Param Life to share these practices with others who seek long-lasting health, happiness, and fulfilment. Param has conducted multiple programs both offline and online, and aims to make this revolution a global phenomenon.`,
          ].map((para, i) => (
            <p key={i} style={{ fontSize: 15, color: T.text2, lineHeight: 1.75, margin: 0 }}>{para}</p>
          ))}
        </div>
      </section>

      {/* ══ MISSION / VISION / GOAL ══ */}
      <section style={{ background: T.cream2, padding: "56px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <SectionLabel>What drives us</SectionLabel>
            <h2 style={{  fontSize: "clamp(22px, 4vw, 34px)", fontWeight: 400, marginTop: 12, color: T.brown }}>
              Mission, Vision & Goal
            </h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {pillars.map((item, i) => (
              <div key={i} style={{
                ...styles.card,
                display: "flex",
                gap: 24,
                alignItems: "center",
                flexWrap: "wrap",
              }}>

                <div style={{ flex: 1, minWidth: 200 }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: T.orange, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                    {item.title}
                  </p>
                  <p style={{ fontSize: 15, color: T.text2, lineHeight: 1.7, margin: 0 }}>{item.text}</p>
                </div>
                <div style={{ width: 140, flexShrink: 0 }}>
                  <img
                    src={item.image}
                    alt={item.title}
                    style={{ width: "100%", borderRadius: 12, objectFit: "cover", maxHeight: 100 }}
                    onError={e => { e.target.style.display = "none"; }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ VALUES ══ */}
      <section style={{ padding: "56px 24px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <SectionLabel>What we stand for</SectionLabel>
            <h2 style={{ fontSize: "clamp(22px, 4vw, 34px)", fontWeight: 400, marginTop: 12, color: T.brown }}>
              Our values
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            {values.map((v, i) => (
              <div key={i} style={{
                ...styles.cardSm,
                display: "flex",
                gap: 14,
                alignItems: "flex-start",
                borderLeft: `3px solid ${T.orange}`,
                borderRadius: "0 16px 16px 0",
              }}>
                <FaHeart style={{ color: T.orange, marginTop: 3, flexShrink: 0, fontSize: 14 }} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14, color: T.brown, marginBottom: 4 }}>{v.title}</p>
                  <p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6, margin: 0 }}>{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TEAM ══ */}
      <section style={{ background: T.cream2, padding: "56px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <SectionLabel>The people</SectionLabel>
            <h2 style={{  fontSize: "clamp(22px, 4vw, 34px)", fontWeight: 400, marginTop: 12, color: T.brown }}>
              Our team
            </h2>
          </div>

          {/* Founders */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20, marginBottom: 48 }}>
            {founders.map((m, i) => (
              <a key={i} href={m.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <div style={{
                  ...styles.card,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 12,
                  transition: "box-shadow .2s",
                  cursor: "pointer",
                }}>
                  <Avatar image={m.image} initials={m.initials} size={96} />
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 16, color: T.brown, marginBottom: 4 }}>{m.name}</p>
                    <p style={{ fontSize: 13, color: T.orange, fontWeight: 500, marginBottom: 4 }}>{m.role}</p>
                    <p style={{ fontSize: 12, color: T.gray }}>{m.education}</p>
                  </div>
                  <span style={{ ...styles.pill, fontSize: 11 }}>View LinkedIn ↗</span>
                </div>
              </a>
            ))}
          </div>

          {/* Advisors */}
          <div style={{ marginBottom: 48 }}>
            <h3 style={{  fontSize: 24, fontWeight: 400, color: T.brown, textAlign: "center", marginBottom: 24 }}>
              Visionary advisors
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
              {advisors.map((m, i) => (
                <a key={i} href={m.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <div style={{
                    background: T.white,
                    borderRadius: 16,
                    border: `1px solid ${T.grayLight}`,
                    padding: "20px 16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                    textAlign: "center",
                    transition: "box-shadow .2s",
                    cursor: "pointer",
                  }}>
                    <Avatar image={m.image} initials={m.initials} size={72} />
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 14, color: T.brown, marginBottom: 3 }}>{m.name}</p>
                      <p style={{ fontSize: 12, color: T.gray }}>{m.role}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Patrons */}
          <div>
            <h3 style={{ fontSize: 24, fontWeight: 400, color: T.brown, textAlign: "center", marginBottom: 24 }}>
              Passionate patrons
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
              {patrons.map((m, i) => (
                <a key={i} href={m.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <div style={{
                    background: T.white,
                    borderRadius: 14,
                    border: `1px solid ${T.grayLight}`,
                    padding: "18px 12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    textAlign: "center",
                    cursor: "pointer",
                  }}>
                    <Avatar image={m.image} initials={m.initials} size={60} />
                    <p style={{ fontWeight: 600, fontSize: 13, color: T.brown, margin: 0 }}>{m.name}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ CONTACT / ADDRESSES ══ */}
      <section style={{ padding: "56px 24px 64px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <SectionLabel>Find us</SectionLabel>
            <h2 style={{  fontSize: "clamp(22px, 4vw, 34px)", fontWeight: 400, marginTop: 12, color: T.brown }}>
              Our offices
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
            {[
              {
                label: "Corporate address",
                addr: ["No. 289, Arcadia Grant,", "Badowala, Prem Nagar,", "Dehradun, Uttarakhand — 248007"],
              },
              {
                label: "Registered address",
                addr: ["No. 2539, Shivpuri,", "Kurwar Road, Niralanagar,", "Sultanpur, Uttar Pradesh — 228001"],
              },
            ].map((loc, i) => (
              <div key={i} style={{ ...styles.cardSm, display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: T.orangeLight,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <IoLocationOutline style={{ color: T.orange, fontSize: 20 }} />
                </div>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: T.orange, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                    {loc.label}
                  </p>
                  {loc.addr.map((line, j) => (
                    <p key={j} style={{ fontSize: 14, color: T.text2, lineHeight: 1.6, margin: 0 }}>{line}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CTA strip */}
          <div style={{
            marginTop: 40,
            background: T.brown,
            borderRadius: 20,
            padding: "32px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 20,
          }}>
            <div>
              <p style={{ fontSize: 11, color: "rgba(255,248,238,.5)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                Ready to begin?
              </p>
              <p style={{   fontSize: 22, color: "#FFF8EE", fontWeight: 400, margin: 0, lineHeight: 1.3 }}>
                Start your journey to inner mastery
              </p>
            </div>
            <button style={styles.orangeBtn}>
              Explore Param App →
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;
 